import type { Action } from '../App/types';
import type { PeopleProfileState } from './types';
import * as ActionTypes from './constants';
import moment from 'moment';

// Helper function to compute nextActivity from pending activities
const computeNextActivity = (pendingActivities: Array<any>) => {
  if (!pendingActivities || pendingActivities.length === 0) {
    return null;
  }

  // Sort by startTime ascending and get the earliest one
  const sorted = [...pendingActivities].sort((a, b) => {
    const timeA = moment(a.startTime);
    const timeB = moment(b.startTime);
    return timeA.diff(timeB);
  });

  return sorted[0];
};

// Helper to update prospect with computed nextActivity
const updateProspectWithNextActivity = (prospect: any, nextActivity: any) => {
  if (!prospect) return prospect;

  return {
    ...prospect,
    nextActivity: nextActivity,
  };
};

const initialState = {
  currentProspect: undefined,
  pendingActivities: [],
  completedActivities: [],
  allActivities: [],
  completedActivitiesMeta: {
    count: 0,
    pageCount: 0,
    totalCount: 0,
    next: '',
    previous: '',
    self: '',
    first: '',
    last: '',
  },
  allActivitiesMeta: {
    count: 0,
    pageCount: 0,
    totalCount: 0,
    next: '',
    previous: '',
    self: '',
    first: '',
    last: '',
  },
  columnOrder: {
    status: 'sortable',
    type: 'sortable',
    startTime: 'sortable',
  },
};

const peopleProfileReducer = (
  state: PeopleProfileState = initialState,
  action: Action<*, *>,
): PeopleProfileState => {
  const { type, payload, meta } = action;
  const defaultColumnOrder = { startTime: 'sortable' };
  switch (type) {
    case ActionTypes.GET_ONE_PROSPECT_SUCCESS: {
      // When we get a prospect, compute nextActivity from current pending activities
      // if we have them, otherwise use the one from the backend
      const nextActivity =
        state.pendingActivities.length > 0
          ? computeNextActivity(state.pendingActivities)
          : payload.nextActivity;

      return {
        ...state,
        currentProspect: {
          ...payload,
          nextActivity: nextActivity || payload.nextActivity,
        },
      };
    }
    case ActionTypes.GET_ONE_PROSPECT_ERROR: {
      return { ...state, currentProspect: initialState.currentProspect };
    }
    case ActionTypes.GET_PROSPECT_PENDING_ACTIVITIES_SUCCESS: {
      // When pending activities are fetched, compute nextActivity and update prospect
      const nextActivity = computeNextActivity(payload);
      const updatedProspect = state.currentProspect
        ? updateProspectWithNextActivity(state.currentProspect, nextActivity)
        : state.currentProspect;

      return {
        ...state,
        pendingActivities: payload,
        currentProspect: updatedProspect,
      };
    }
    case ActionTypes.GET_PROSPECT_COMPLETED_ACTIVITIES_SUCCESS: {
      return {
        ...state,
        completedActivities: payload,
        completedActivitiesMeta: meta,
      };
    }
    case ActionTypes.GET_PROSPECT_ALL_ACTIVITIES_SUCCESS: {
      return { ...state, allActivities: payload, allActivitiesMeta: meta };
    }
    case ActionTypes.UPDATE_COLUMNS_SORT_VALUE: {
      return {
        ...state,
        columnOrder: Object.assign({}, defaultColumnOrder, payload),
      };
    }
    default:
      return state;
  }
};

export default peopleProfileReducer;
